import React, { useState, useRef, useEffect } from "react";
import { FaUtensils, FaTimes, FaChevronRight, FaChevronLeft, FaCheck, FaChevronDown } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserMeals, fetchUserMealPlans, selectUserMeals, selectUserMealPlans, selectUserMealPlansStatus } from '../redux/userSlice';
import { CheckCircle, Activity, Zap, ChevronDown } from "lucide-react";
import fitnessVideo from '../images/fitness.mp4';

const CalorieCalculator = ({ products: propProducts = [], mealPlansData = null }) => {
  const dispatch = useDispatch();
  const reduxProducts = useSelector(selectUserMeals);
  const reduxMealPlans = useSelector(selectUserMealPlans);
  const mealPlansStatus = useSelector(selectUserMealPlansStatus);

  const products = propProducts.length > 0 ? propProducts : reduxProducts;
  const mealPlansSource = mealPlansData !== null ? mealPlansData : reduxMealPlans;

  const [personalInfo, setPersonalInfo] = useState({
    name: "",
    age: "",
    height: "",
    weight: "",
    gender: "male",
    activityLevel: "sedentary",
  });
  const [calculatedCalories, setCalculatedCalories] = useState(0);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedMealPlans, setSelectedMealPlans] = useState([]);
  const scrollRef = useRef(null);
  const [mealPlans, setMealPlans] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();


  // --- Carousel State ---
  const [currentFoodIndex, setCurrentFoodIndex] = useState(0);
  const visibleFoodCards = 4;
  const totalFoodCards = products.length;

  const nextFood = () => {
    if (currentFoodIndex < totalFoodCards - visibleFoodCards) {
      setCurrentFoodIndex(currentFoodIndex + 1);
    }
  };

  const prevFood = () => {
    if (currentFoodIndex > 0) {
      setCurrentFoodIndex(currentFoodIndex - 1);
    }
  };

  const activityLevels = {
    sedentary: 1.2,
    light: 1.375,
    moderate: 1.55,
    active: 1.725,
    veryActive: 1.9
  };

  // Helper to process meal plans array into component state
  const processMealPlansData = (data) => {
    const uniqueCategories = [];
    data.forEach(mealPlan => {
      if (mealPlan.categoryDetails && mealPlan.categoryDetails.length > 0) {
        mealPlan.categoryDetails.forEach(category => {
          const existingCategory = uniqueCategories.find(c => c.id === category.identifier);
          if (!existingCategory) {
            uniqueCategories.push({
              id: category.identifier,
              label: category.categoryName,
              icon: <FaUtensils />,
              color: "from-lime-600 to-lime-800"
            });
          }
        });
      }
    });
    setMealPlans(data);
    setCategories(uniqueCategories);
    if (uniqueCategories.length > 0 && !activeCategory) {
      setActiveCategory(uniqueCategories[0].id);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    dispatch(fetchUserMeals());
    dispatch(fetchUserMealPlans());
  }, [dispatch]);

  useEffect(() => {
    if (mealPlansSource && mealPlansSource.length > 0) {
      processMealPlansData(mealPlansSource);
    } else if (mealPlansStatus === 'succeeded' || mealPlansStatus === 'failed') {
      setIsLoading(false);
    }
  }, [mealPlansSource, mealPlansStatus]);

  const handleChange = (e) => {
    setPersonalInfo({ ...personalInfo, [e.target.name]: e.target.value });
  };

  const calculatePersonalCalories = (event) => {
    if (event) event.preventDefault();
    const { age, height, weight, gender, activityLevel } = personalInfo;
    let bmr = gender === "male"
      ? 10 * weight + 6.25 * height - 5 * age + 5
      : 10 * weight + 6.25 * height - 5 * age - 161;

    setCalculatedCalories(Math.round(bmr * activityLevels[activityLevel]));
    setShowSummary(true);
  };

  const addToPlan = (mealPlan) => {
    setSelectedMealPlans([...selectedMealPlans, mealPlan]);
    navigate('/checkout');
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const { current } = scrollRef;
      const scrollAmount = direction === 'left' ? -300 : 300;
      current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Filter meal plans by active category
  const filteredMealPlans = mealPlans.filter(mealPlan =>
    mealPlan.category && mealPlan.category.includes(activeCategory)
  );

  // Default categories if API doesn't return any
  const defaultCategories = [
    { id: "default", label: "Meal Plans", icon: <FaUtensils />, color: "from-lime-600 to-lime-800" }
  ];

  // Use API categories or fallback to defaults
  const displayCategories = categories.length > 0 ? categories : defaultCategories;

  return (
    <div className="relative py-12 font-dosis ">
      <div className="max-w-full xl:mx-auto  xl:px-4  lg:px-8">

        {/* Main "Magazine" Container */}
        <div className="relative lg:rounded-t-[3rem] overflow-hidden shadow-2xl min-h-[600px] flex flex-col">

          {/* Background Image/Video Layer */}
          <div className="absolute inset-0 z-0">
            <video
              src={fitnessVideo}
              autoPlay
              loop
              muted
              playsInline
              preload="none"
              className="w-full h-full object-cover"
            />
            {/* Grading Overlay - Darker on right for text readability */}
            <div className="absolute  lg:rounded-t-[3rem] inset-0 bg-gradient-to-r from-black/40 via-black/60 to-black/80"></div>
          </div>

          <div className="relative z-10 w-full grid grid-cols-1 lg:grid-cols-2">

            {/* Left Side: Inspiration */}
            <div className="p-10  flex flex-col justify-center  text-white space-y-6">
              <div className="inline-flex items-center px-4 py-1 rounded-full bg-white/20 backdrop-blur-md w-fit border border-white/10">
                <span className="text-sm font-bold tracking-wider uppercase text-white">Science meets Nature</span>
              </div>

              <h2 className="text-5xl md:text-7xl font-bold leading-tight">
                Fuel Your
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-300 to-emerald-400">Ambition</span>
              </h2>

              <p className="text-lg md:text-xl text-gray-200 max-w-md leading-relaxed font-light">
                "True health isn't about restriction. It's about finding the precise energy balance that lets your body thrive. Every meal we prepare is a calculated step toward your peak performance. We blend nutritional science with culinary artistry to ensure you never have to choose between health and flavor. Experience a lifestyle where your nutrition works as hard as you do."
              </p>
            </div>

            {/* Right Side: The "Glass" Calculator */}

            <div className="relative mt-10 border-2 border-white rounded-xl  lg:mx-40 backdrop-blur-xl bg-black/30 border-l border-white/10 p-10 md:p-16 flex flex-col justify-center">

              <div className="mb-10">
                <h3 className="text-3xl font-bold text-white mb-2">My Daily Target</h3>
                <p className="text-white/60">Enter your metrics to unlock your plan.</p>
              </div>

              <form className="space-y-8" onSubmit={calculatePersonalCalories}>
                {/* Gender Selector - Text Style */}
                <div className="flex gap-8 border-b border-white/20 pb-4">
                  {['male', 'female'].map(g => (
                    <label key={g} className={`cursor-pointer text-xl font-bold uppercase tracking-widest transition-all ${personalInfo.gender === g ? 'text-green-400 scale-105' : 'text-white/40 hover:text-white'}`}>
                      <input type="radio" name="gender" value={g} onChange={handleChange} className="hidden" defaultChecked={personalInfo.gender === g} />
                      {g}
                    </label>
                  ))}
                </div>

                {/* Inputs - Material Style Line Inputs */}
                <div className="grid grid-cols-2 gap-x-8 gap-y-10">
                  <div className="relative group">
                    <input
                      type="number"
                      name="age"
                      placeholder=" "
                      onChange={handleChange}
                      className="block py-2.5 px-0 w-full text-xl text-white bg-transparent border-0 border-b-2 border-white/20 appearance-none focus:outline-none focus:ring-0 focus:border-green-400 peer"
                    />
                    <label className="peer-focus:font-medium absolute text-sm text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-green-400 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6">Age</label>
                  </div>

                  <div className="relative group">
                    <select name="activityLevel" onChange={handleChange} className="block py-2.5 px-0 w-full text-xl text-white bg-transparent border-0 border-b-2 border-white/20 appearance-none focus:outline-none focus:ring-0 focus:border-green-400 peer cursor-pointer [&>option]:text-black">
                      <option value="sedentary">Sedentary</option>
                      <option value="light">Light Activity</option>
                      <option value="moderate">Moderate</option>
                      <option value="active">Active</option>
                      <option value="veryActive">Very Active</option>
                    </select>
                    <label className="peer-focus:font-medium absolute text-sm text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-green-400 peer-placeholder-shown:scale-75 peer-placeholder-shown:-translate-y-6">Lifestyle</label>
                    <div className="absolute right-0 bottom-3 text-white/50 pointer-events-none"><ChevronDown size={14} /></div>
                  </div>

                  <div className="relative group">
                    <input
                      type="number"
                      name="height"
                      placeholder=" "
                      onChange={handleChange}
                      className="block py-2.5 px-0 w-full text-xl text-white bg-transparent border-0 border-b-2 border-white/20 appearance-none focus:outline-none focus:ring-0 focus:border-green-400 peer"
                    />
                    <label className="peer-focus:font-medium absolute text-sm text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-green-400 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6">Height (cm)</label>
                  </div>

                  <div className="relative group">
                    <input
                      type="number"
                      name="weight"
                      placeholder=" "
                      onChange={handleChange}
                      className="block py-2.5 px-0 w-full text-xl text-white bg-transparent border-0 border-b-2 border-white/20 appearance-none focus:outline-none focus:ring-0 focus:border-green-400 peer"
                    />
                    <label className="peer-focus:font-medium absolute text-sm text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-green-400 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6">Weight (kg)</label>
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={!acceptedTerms}
                    className={`group relative w-full flex justify-center py-4 px-4 border border-transparent text-lg font-bold rounded-full text-black bg-white hover:bg-green-400 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white ${!acceptedTerms && 'opacity-50 cursor-not-allowed'}`}
                  >
                    <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                      <Activity className="h-5 w-5 text-gray-500 group-hover:text-black transition-colors" />
                    </span>
                    Calculate Plan
                  </button>

                  <label className="flex items-center justify-center gap-3 mt-4 cursor-pointer group">
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all ${acceptedTerms ? 'bg-green-400 border-green-400' : 'border-white/30 bg-transparent'}`}>
                      {acceptedTerms && <FaCheck size={10} className="text-black" />}
                    </div>
                    <input type="checkbox" checked={acceptedTerms} onChange={() => setAcceptedTerms(!acceptedTerms)} className="hidden" />
                    <span className="text-xs text-white/50 group-hover:text-white transition-colors uppercase tracking-widest">I agree to Terms</span>
                  </label>
                </div>

              </form>

            </div>

          </div>

          {/* --- Featured Meals Section (Inside the Video Wrapper) --- */}
          <div className="relative z-10 w-full px-2 py-12 pb-24">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-white mb-2 drop-shadow-lg">Featured Healthy Meals</h2>
              <p className="text-white/80 max-w-2xl mx-auto drop-shadow-md">
                Discover our nutritionist-approved meals designed for your fitness goals.
              </p>
            </div>

            {products.length > 0 ? (
              <div className="relative overflow-hidden px-5 min-h-[400px]">
                <div
                  className="flex space-x-6 md:space-x-12 transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${currentFoodIndex * 320}px)` }}
                >
                  {products.map((product, index) => (
                    <div key={product._id} className="flex-shrink-0 w-72 md:w-80 relative mt-12 mb-8 group">
                      {/* Card Base */}
                      <div className="bg-white/10 backdrop-blur-xl rounded-[2rem] shadow-xl hover:shadow-2xl hover:bg-white/20 transition-all duration-500 p-6 h-[380px] flex flex-col justify-end relative overflow-visible border border-white/20">

                        {/* Newly Added Badge */}
                        <div className="absolute top-4 left-4 flex flex-col gap-2 z-20">
                          {index < 4 && (
                            <div className="bg-lime-500 text-white text-[10px] font-bold px-3 py-1.5 rounded-full shadow-lg uppercase tracking-wider text-center">
                              New
                            </div>
                          )}
                          {product.foodType && (
                            <div className={`text-white text-[10px] font-bold px-3 py-1.5 rounded-full shadow-lg uppercase tracking-wider text-center border backdrop-blur-md ${product.foodType.toLowerCase() === 'veg' ? 'bg-green-600/80 border-green-400/50' :
                              product.foodType.toLowerCase() === 'non-veg' ? 'bg-red-600/80 border-red-400/50' :
                                'bg-yellow-600/80 border-yellow-400/50'
                              }`}>
                              {product.foodType}
                            </div>
                          )}
                        </div>

                        {/* Floating Food Image */}
                        <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 w-48 h-48 z-10 transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-3">
                          <div className="relative w-full h-full">
                            {/* Glow effect */}
                            <div className="absolute inset-4 bg-white/20 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                            
                            {product.image && product.image[0] ? (
                              <img
                                src={product.image[0]}
                                alt={product.mealName || "Healthy Meal"}
                                className="w-full h-full object-cover rounded-full shadow-2xl filter drop-shadow-xl border-4 border-white"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.style.display = 'none';
                                  e.target.parentNode.innerHTML += `
                                    <div class="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 border-4 border-white flex flex-col items-center justify-center rounded-full shadow-2xl">
                                      <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-slate-300 mb-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                      <span class="text-slate-400 text-[10px] font-bold uppercase tracking-wider">No Image</span>
                                    </div>`;
                                }}
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 border-4 border-white flex flex-col items-center justify-center rounded-full shadow-2xl">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-slate-300 mb-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider">No Image</span>
                              </div>
                            )}

                          </div>
                        </div>

                        {/* Content Area */}
                        <div className="pt-32 text-center h-full flex flex-col">
                          {/* Tags */}
                          {product.mealTypeDetails && Array.isArray(product.mealTypeDetails) && product.mealTypeDetails.length > 0 && (
                            <div className="flex flex-wrap justify-center gap-2 mb-3">
                              {product.mealTypeDetails.slice(0, 2).map((typeObj, idx) => (
                                typeObj.mealType ? (
                                  <span key={idx} className="bg-black/40 text-white text-[10px] font-bold px-2 py-1 rounded-lg uppercase tracking-wide">
                                    {typeObj.mealType}
                                  </span>
                                ) : null
                              ))}
                            </div>
                          )}

                          <h3 className="text-xl font-bold text-white mb-1 group-hover:text-lime-300 transition-colors line-clamp-1 capitalize drop-shadow-md">
                            {product.mealName || "Healthy Meal"}
                          </h3>

                          {product.mealPlanName && (
                            <div className="text-white/60 text-xs font-semibold mb-3 tracking-wider uppercase">
                              {product.mealPlanName}
                            </div>
                          )}

                          <p className="text-white/80 text-sm leading-relaxed line-clamp-2 mb-4">
                            {product.description || "The perfect combination of nutrition and taste."}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Navigation arrows (Styled for Dark Background) */}
                <button
                  onClick={prevFood}
                  disabled={currentFoodIndex === 0}
                  className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-white/10 backdrop-blur-md p-3 rounded-full shadow-lg text-white hover:bg-lime-500 transition-colors disabled:opacity-30 disabled:cursor-not-allowed border border-white/20"
                >
                  <FaChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={nextFood}
                  disabled={currentFoodIndex >= totalFoodCards - visibleFoodCards}
                  className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-white/10 backdrop-blur-md p-3 rounded-full shadow-lg text-white hover:bg-lime-500 transition-colors disabled:opacity-30 disabled:cursor-not-allowed border border-white/20"
                >
                  <FaChevronRight className="w-6 h-6" />
                </button>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-white/60 font-semibold">Loading delicious meals...</p>
              </div>
            )}
          </div>

        </div>
      </div>


      {/* --- RESULTS MODAL (Existing logic hooked into new flow) --- */}
      {/* 
          NOTE: We are reusing the existing 'showSummary' state. 
          The previous AnimatePresence block for results can remain or be integrated.
          For cleanliness, I'm ensuring the 'showSummary' modal appears ON TOP or 
          replaces the calculator modal. 
      */}
      <AnimatePresence>
        {showSummary && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] flex flex-col"
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-lime-700 to-lime-900 p-6 text-white rounded-t-3xl">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold">Your Nutrition Plan</h2>
                  <button
                    onClick={() => setShowSummary(false)}
                    className="p-1 rounded-full hover:bg-black/10 transition-colors"
                  >
                    <FaTimes />
                  </button>
                </div>
                <div className="mt-4 bg-white/20 p-4 rounded-xl backdrop-blur-sm">
                  <p className="text-sm opacity-90">Daily Calorie Needs</p>
                  <p className="text-4xl font-bold">{calculatedCalories} <span className="text-2xl">kcal</span></p>
                </div>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-hidden flex flex-col">
                {/* Categories */}
                {displayCategories.length > 0 && (
                  <div className="px-6 pt-4 pb-2 flex space-x-2 overflow-x-auto scrollbar-hide">
                    {displayCategories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => setActiveCategory(category.id)}
                        className={`flex items-center px-4 py-2 rounded-full transition-all ${activeCategory === category.id
                          ? `bg-gradient-to-r ${category.color} text-white shadow-md`
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      >
                        <span className="mr-2">{category.icon}</span>
                        {category.label}
                      </button>
                    ))}
                  </div>
                )}

                {/* Meal Plans */}
                <div className="relative flex-1 px-6 py-4 overflow-hidden">
                  {isLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">Loading meal plans...</p>
                    </div>
                  ) : filteredMealPlans.length > 0 ? (
                    <div
                      ref={scrollRef}
                      className="h-full overflow-x-auto scrollbar-hide whitespace-nowrap space-x-4 pb-4"
                    >
                      {filteredMealPlans.map((mealPlan, index) => (
                        <div
                          key={index}
                          className="inline-block w-64 h-full align-top whitespace-normal"
                        >
                          <motion.div
                            whileHover={{ y: -5 }}
                            className="bg-white rounded-xl shadow-md h-full flex flex-col border border-gray-100 overflow-hidden"
                          >
                            <div className="h-20 overflow-hidden">
                              <img
                                src={mealPlan.image && mealPlan.image.length > 0 && mealPlan.image[0]
                                  ? mealPlan.image[0]
                                  : "https://source.unsplash.com/500x300/?food"}
                                alt={mealPlan.mealPlanName}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="p-4 flex-1 flex flex-col">
                              <h3 className="font-bold text-lg text-gray-800">{mealPlan.mealPlanName}</h3>
                              <p className="text-gray-600 text-sm line-clamp-2 mt-1">{mealPlan.description}</p>
                              <div className="mt-auto">
                                <button
                                  onClick={() => addToPlan(mealPlan)}
                                  className="mt-4 bg-gradient-to-r from-lime-600 to-lime-800 text-white py-2 rounded-lg font-medium w-full hover:opacity-90 transition-opacity"
                                >
                                  Add to Plan
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No meal plans available in this category</p>
                    </div>
                  )}

                  {/* Custom Scroll Arrows */}
                  {filteredMealPlans.length > 1 && (
                    <>
                      <button
                        onClick={() => scroll('left')}
                        className="absolute left-6 top-1/2 -translate-y-1/2 bg-white w-10 h-10 rounded-full shadow-md flex items-center justify-center text-gray-600 hover:text-lime-600 transition-colors"
                      >
                        <FaChevronLeft />
                      </button>
                      <button
                        onClick={() => scroll('right')}
                        className="absolute right-6 top-1/2 -translate-y-1/2 bg-white w-10 h-10 rounded-full shadow-md flex items-center justify-center text-gray-600 hover:text-lime-600 transition-colors"
                      >
                        <FaChevronRight />
                      </button>
                    </>
                  )}
                </div>

                {/* Selected Meal Plans */}
                {selectedMealPlans.length > 0 && (
                  <div className="border-t border-gray-200 p-4">
                    <h3 className="font-semibold text-gray-700 mb-2">Your Selected Meal Plans</h3>
                    <div className="space-y-2">
                      {selectedMealPlans.map((mealPlan, index) => (
                        <div key={index} className="flex justify-between items-center bg-gray-50 p-3 rounded-lg">
                          <span className="text-gray-700">{mealPlan.mealPlanName}</span>
                          <button
                            onClick={() => {
                              const updated = [...selectedMealPlans];
                              updated.splice(index, 1);
                              setSelectedMealPlans(updated);
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CalorieCalculator;